// AP-MED-04 — مشترک نسخه‌نویسی پرحجم و تأیید از راه دور استاد (US-MED-080 تا 091): تاریخ اجرا، تکرار آزمایش، Modifier
// حاجب و ناحیه، پیشنهاد هم‌تجویزی، دیکته، پایش ارسال‌ها و صندوق تأیید. متن‌ها فقط از فضای نام med.
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  unwrap,
  type MedContrastModifier,
  type MedEffectiveDateMode,
  type MedEffectiveDateOptionsResponse,
  type MedEffectiveDateRequest,
  type MedLaterality,
  type MedOrderItemRequest,
  type MedRxMonitorStatus,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { BusinessDateField, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { API } from './common';

/** پایش ارسال‌های ملی (US-MED-086). */
export const SCR_STAFF_710 = 'SCR-STAFF-710';
/** ویزیت فقط نسخه (US-MED-088). */
export const SCR_STAFF_711 = 'SCR-STAFF-711';
/** پیگیری تهیه‌کننده پیش‌نویس‌های ارسالی (US-MED-089). */
export const SCR_STAFF_712 = 'SCR-STAFF-712';
/** صندوق تأیید از راه دور استاد در PWA (US-MED-090). */
export const SCR_STAFF_714 = 'SCR-STAFF-714';

export const RX_MONITOR_PATH = '/desk/rx-monitor';
export const APPROVAL_INBOX_PATH = '/desk/approvals/inbox';
export const MY_APPROVALS_PATH = '/desk/approvals/mine';
export const RX_ONLY_PATH = '/desk/rx-only-visit';

export const highVolumeKeys = {
  effectiveOptions: (tenantId: string | undefined, insurer: string | null) => ['medorders', tenantId, 'effective-options', insurer] as const,
  monitor: (tenantId: string | undefined, filter: string) => ['medorders', tenantId, 'rx-monitor', filter] as const,
  approvers: (tenantId: string | undefined, encounterId: string) => ['medorders', tenantId, 'approvers', encounterId] as const,
  inbox: (tenantId?: string) => ['medorders', tenantId, 'approvals', 'inbox'] as const,
  mine: (tenantId?: string) => ['medorders', tenantId, 'approvals', 'mine'] as const,
  coPrescriptions: (tenantId: string | undefined, drugCode: string) => ['medorders', tenantId, 'co-prescriptions', drugCode] as const,
  voice: (tenantId?: string) => ['medorders', tenantId, 'voice-availability'] as const,
  frequent: (tenantId: string | undefined, kind: string) => ['medorders', tenantId, 'frequent', kind] as const,
};

export const CONTRAST_MODIFIERS: readonly NonNullable<MedContrastModifier>[] = ['Without', 'With', 'WithAndWithout'];
export const LATERALITIES: readonly NonNullable<MedLaterality>[] = ['Right', 'Left', 'Bilateral'];

/** رنگ وضعیت پایش (SCR-STAFF-710). */
export function monitorTone(status: NonNullable<MedRxMonitorStatus>): StatusTone {
  switch (status) {
    case 'Registered':
      return 'done';
    case 'Dispensed':
      return 'active';
    case 'Failed':
      return 'error';
    case 'Voided':
      return 'cancelled';
    case 'NotSent':
      return 'inactive';
    default:
      return 'pending';
  }
}

/** گزینه‌های تاریخ اجرا و پرچم‌های قابلیت Adapter بیمه‌گر (AC-MED-081.1 تا 081.3). */
export function useEffectiveDateOptions(insurer: string | null, enabled = true) {
  const { tenantApi, session } = useTenantSession();

  return useQuery({
    queryKey: highVolumeKeys.effectiveOptions(session?.tenantId, insurer),
    enabled: Boolean(session?.tenantId) && enabled,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/effective-date-options', { params: { query: insurer ? { insurer } : {} } })),
  });
}

/** مقدار فرم تاریخ اجرا (حالت و مقدار). */
export interface EffectiveDraft {
  mode: MedEffectiveDateMode;
  window: string;
  date: string | null;
  locked: boolean;
}

export const TODAY_EFFECTIVE: EffectiveDraft = { mode: 'Today', window: '', date: null, locked: false };

/** تبدیل فرم به بدنه درخواست؛ «امروز» تهی می‌فرستد تا رفتار پیشین عوض نشود (AC-MED-081.4). */
export function toEffectiveRequest(draft: EffectiveDraft): MedEffectiveDateRequest | null {
  if (draft.mode === 'Today' && !draft.locked) {
    return null;
  }
  return { mode: draft.mode, window: draft.mode === 'Window' ? draft.window || null : null, date: draft.mode === 'Exact' ? draft.date : null, locked: draft.locked };
}

/**
 * انتخاب تاریخ اجرا (AC-MED-081.1): «امروز» (پیش‌فرض)، «بازه تقریبی» از بازه‌های تنظیم‌شده مستأجر و «تاریخ دقیق» با
 * تقویم شمسی. در نبود قابلیت Adapter بیمه‌گر، هشدار «ارسال ملی ممکن نیست» نمایش داده می‌شود (AC-MED-081.3).
 */
export function EffectiveDateField({
  value,
  options,
  onChange,
  testId = 'med-effective',
}: {
  value: EffectiveDraft;
  options: MedEffectiveDateOptionsResponse | undefined;
  onChange: (next: EffectiveDraft) => void;
  testId?: string;
}) {
  const { t } = useTranslation('med');
  const windows = Object.keys(options?.windows ?? {});

  return (
    <Stack spacing={1} data-testid={testId}>
      <Stack direction="row" spacing={1}>
        <TextField
          select
          fullWidth
          size="small"
          label={t('med:effective.mode')}
          value={value.mode}
          onChange={(event) => onChange({ ...value, mode: event.target.value as MedEffectiveDateMode })}
          slotProps={{ htmlInput: { 'data-testid': `${testId}-mode` } }}
        >
          <MenuItem value="Today">{t('med:effective.today')}</MenuItem>
          <MenuItem value="Window">{t('med:effective.window')}</MenuItem>
          <MenuItem value="Exact">{t('med:effective.exact')}</MenuItem>
        </TextField>
        {value.mode === 'Window' ? (
          <TextField
            select
            fullWidth
            size="small"
            label={t('med:effective.window')}
            value={value.window}
            onChange={(event) => onChange({ ...value, window: event.target.value })}
            slotProps={{ htmlInput: { 'data-testid': `${testId}-window` } }}
          >
            {windows.map((key) => (
              <MenuItem key={key} value={key}>
                {t(`med:effective_window.${key}`)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {value.mode === 'Exact' ? (
          <BusinessDateField
            label={t('med:effective.date')}
            value={value.date ?? ''}
            onChange={(next) => onChange({ ...value, date: next || null })}
            testId={`${testId}-date`}
          />
        ) : null}
      </Stack>
      {value.mode !== 'Today' && options && !options.supportsFutureEffectiveDate ? (
        <Alert severity="info" data-testid={`${testId}-no-capability`}>
          {t('med:effective.insurer_unsupported')}
        </Alert>
      ) : null}
    </Stack>
  );
}

/**
 * تکرار آزمایش و تاریخ اجرای یک قلم درخواست (AC-MED-082.1): دکمه «+» تعداد را تا سقف مستأجر بالا می‌برد و در تکرار
 * بیش از ۱ فاصله به روز الزامی است.
 */
export function OrderItemRepeatFields({
  item,
  max,
  onChange,
}: {
  item: MedOrderItemRequest;
  max: number;
  onChange: (next: MedOrderItemRequest) => void;
}) {
  const { t } = useTranslation('med');
  const count = Number(item.repeatCount ?? 1);

  return (
    <Stack direction="row" spacing={1} alignItems="center" data-testid={`med-order-repeat-${item.code}`}>
      <Typography variant="body2" sx={{ minWidth: 120 }}>
        {item.name}
      </Typography>
      <IconButton
        size="small"
        aria-label={t('med:order.repeat_less')}
        disabled={count <= 1}
        onClick={() => onChange({ ...item, repeatCount: Math.max(1, count - 1), repeatIntervalDays: count - 1 <= 1 ? null : item.repeatIntervalDays })}
        data-testid={`med-order-repeat-minus-${item.code}`}
      >
        <RemoveCircleOutlineIcon fontSize="small" />
      </IconButton>
      <Chip size="small" label={count} data-testid={`med-order-repeat-count-${item.code}`} />
      <IconButton
        size="small"
        aria-label={t('med:order.repeat_more')}
        disabled={count >= max}
        onClick={() => onChange({ ...item, repeatCount: Math.min(max, count + 1), repeatIntervalDays: item.repeatIntervalDays ?? 30 })}
        data-testid={`med-order-repeat-plus-${item.code}`}
      >
        <AddCircleOutlineIcon fontSize="small" />
      </IconButton>
      {count > 1 ? (
        <TextField
          size="small"
          type="number"
          label={t('med:order.repeat_interval')}
          value={item.repeatIntervalDays ?? ''}
          onChange={(event) => onChange({ ...item, repeatIntervalDays: event.target.value === '' ? null : Number(event.target.value) })}
          slotProps={{ htmlInput: { min: 1, max: 365, 'data-testid': `med-order-interval-${item.code}` } }}
          sx={{ maxWidth: 140 }}
        />
      ) : null}
    </Stack>
  );
}

/**
 * Modifier حاجب و ناحیه یک قلم تصویربرداری (AC-MED-083.1، 083.2). الزام از پرچم‌های فهرست پاراکلینیک می‌آید و سرور
 * آن را با <c>PHX-MED-0323</c> و <c>PHX-MED-0324</c> می‌سنجد، پس این فرم همیشه هر دو را پیشنهاد می‌دهد.
 */
export function OrderItemImagingFields({
  item,
  onChange,
}: {
  item: MedOrderItemRequest;
  onChange: (next: MedOrderItemRequest) => void;
}) {
  const { t } = useTranslation('med');

  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" data-testid={`med-order-imaging-${item.code}`}>
      <Typography variant="body2" sx={{ minWidth: 120 }}>
        {item.name}
      </Typography>
      <TextField
        select
        size="small"
        label={t('med:order.contrast')}
        value={item.contrast ?? ''}
        onChange={(event) => onChange({ ...item, contrast: (event.target.value || null) as MedContrastModifier | null })}
        slotProps={{ htmlInput: { 'data-testid': `med-order-contrast-${item.code}` } }}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">{t('med:order.contrast_none')}</MenuItem>
        {CONTRAST_MODIFIERS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`med:contrast.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label={t('med:order.region')}
        value={item.region?.code ?? ''}
        onChange={(event) => onChange({ ...item, region: event.target.value ? { code: event.target.value, label: event.target.value, laterality: item.region?.laterality ?? null } : null })}
        slotProps={{ htmlInput: { maxLength: 40, 'data-testid': `med-order-region-${item.code}` } }}
        sx={{ minWidth: 160 }}
      />
      <TextField
        select
        size="small"
        label={t('med:order.laterality')}
        value={item.region?.laterality ?? ''}
        onChange={(event) => onChange({ ...item, region: { code: item.region?.code ?? '', label: item.region?.label ?? null, laterality: (event.target.value || null) as MedLaterality | null } })}
        slotProps={{ htmlInput: { 'data-testid': `med-order-laterality-${item.code}` } }}
        sx={{ minWidth: 140 }}
      >
        <MenuItem value="">{t('med:order.laterality_none')}</MenuItem>
        {LATERALITIES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`med:laterality.${value}`)}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

/** نشانی جزئیات ارسال (US-INT-110؛ SCR-STAFF-1008) برای هر ردیف پایش (AC-MED-086.4). */
export const CALL_LOG_PATH = '/settings/integration/calls';

/** نشانی API پایش (برای کپی کد رهگیری و پیوندها). */
export const MONITOR_API = `${API}/rx-monitor`;
